<template>
  <div class="tool-management-container">
    <div class="page-header">
      <h2>工具管理</h2>
      <el-button type="primary" @click="showAddToolDialog">
        <el-icon><Plus /></el-icon> 添加工具
      </el-button>
    </div>
    
    <!-- 工具列表 -->
    <el-table
      v-loading="loading"
      :data="tools"
      border
      style="width: 100%; margin-top: 20px;"
    >
      <el-table-column type="index" width="50" align="center" />
      
      <el-table-column label="图标" width="80">
        <template #default="scope">
          <el-image
            style="width: 40px; height: 40px;"
            :src="scope.row.preview_url || '/default-tool-icon.png'"
            fit="contain"
            :preview-src-list="scope.row.preview_url ? [scope.row.preview_url] : []"
            :initial-index="0"
          >
            <template #error>
              <div class="image-error">
                <el-icon><Picture /></el-icon>
              </div>
            </template>
          </el-image>
        </template>
      </el-table-column>
      
      <el-table-column prop="name" label="工具名称" min-width="120" />
      
      <el-table-column prop="description" label="描述" min-width="200">
        <template #default="scope">
          <el-tooltip
            v-if="scope.row.description && scope.row.description.length > 50"
            class="box-item"
            effect="dark"
            :content="scope.row.description"
            placement="top-start"
          >
            <span>{{ scope.row.description.substring(0, 50) }}...</span>
          </el-tooltip>
          <span v-else>{{ scope.row.description || '-' }}</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="download_url" label="下载链接" min-width="150">
        <template #default="scope">
          <el-link
            v-if="scope.row.download_url"
            type="primary"
            :href="scope.row.download_url"
            target="_blank"
          >
            {{ scope.row.download_url.length > 30 ? scope.row.download_url.substring(0, 30) + '...' : scope.row.download_url }}
          </el-link>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="sort_order" label="排序" width="80" align="center" />
      
      <el-table-column prop="status" label="状态" width="100" align="center">
        <template #default="scope">
          <el-tag
            :type="scope.row.status === 'active' ? 'success' : 'info'"
            disable-transitions
          >
            {{ scope.row.status === 'active' ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="180"
        fixed="right"
        align="center"
      >
        <template #default="scope">
          <el-button
            type="primary"
            size="small"
            @click="handleEditTool(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="handleDeleteTool(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
    
    <!-- 添加/编辑工具对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑工具' : '添加工具'"
      width="600px"
      destroy-on-close
    >
      <el-form
        ref="toolFormRef"
        :model="toolForm"
        :rules="toolRules"
        label-width="100px"
        style="max-width: 500px; margin: 0 auto;"
      >
        <el-form-item label="工具名称" prop="name">
          <el-input v-model="toolForm.name" placeholder="请输入工具名称" />
        </el-form-item>
        
        <el-form-item label="工具描述" prop="description">
          <el-input
            v-model="toolForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入工具描述"
          />
        </el-form-item>
        
        <el-form-item label="下载链接" prop="download_url">
          <el-input v-model="toolForm.download_url" placeholder="请输入下载链接" />
        </el-form-item>
        
        <el-form-item label="排序顺序" prop="sort_order">
          <el-input-number v-model="toolForm.sort_order" :min="0" :max="999" />
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="toolForm.status">
            <el-radio :value="'active'">启用</el-radio>
            <el-radio :value="'inactive'">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="工具图标">
          <el-upload
            class="tool-icon-uploader"
            :action="`${backendUrl}/file/upload/image`"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleUploadSuccessV2"
            :on-error="handleUploadError"
            name="file"
          >
            <img v-if="toolForm.image_url" :src="toolForm.image_url" class="tool-icon-preview" />
            <el-icon v-else class="tool-icon-uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div v-if="toolForm.image_url" class="icon-actions">
            <el-button type="danger" size="small" @click="removeIcon">移除图标</el-button>
          </div>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="submitForm">
            确认
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="删除工具"
      width="400px"
    >
      <p>确定要删除工具 "{{ currentTool?.name }}" 吗？此操作无法撤销。</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button type="danger" :loading="deleteLoading" @click="confirmDelete">
            确认删除
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup >
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElNotification } from 'element-plus';
import { Plus, Picture } from '@element-plus/icons-vue';
import requestV2 from '@/utils/requestV2';


const backendUrl = ref(import.meta.env.VITE_JAVA_API_URL)



// 表格数据
const tools = ref([]);
const loading = ref(false);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框控制
const dialogVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const toolFormRef = ref(null);
const toolForm = reactive({
  id: null,
  name: '',
  description: '',
  icon_url: '',
  image_url: '',
  download_url: '',
  sort_order: 0,
  status: 'active',
  icon_file: null, // 用于保存文件对象
  remove_icon: false
});

// 删除对话框
const deleteDialogVisible = ref(false);
const deleteLoading = ref(false);
const currentTool = ref(null);

// 表单验证规则
const toolRules = {
  name: [{ required: true, message: '请输入工具名称', trigger: 'blur' }],
  sort_order: [{ type: 'number', message: '排序必须为数字', trigger: 'blur' }],
  download_url: [
    { 
      // 使用更简单的URL验证规则，避免复杂正则可能导致的性能问题
      validator: (rule, value, callback) => {
        if (!value) {
          callback(); // 空值允许通过
          return;
        }
        
        // 简单检查URL格式
        if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('www.')) {
          callback();
        } else {
          callback(new Error('请输入有效的URL，以http://、https://或www.开头'));
        }
      },
      trigger: 'blur'
    }
  ]
};



const handleUploadError = (error) => {
  console.error('上传失败:', error)
  ElMessage.error('上传失败，请检查网络连接')
}

const handleUploadSuccessV2 = async (response) => {
  if (response.code == 0) {

    // 上传成功
    const objectName =  response.data.objectName;
    const res =  await requestV2.post('/file/getImagePreview', {
      objectName: objectName,
    })
    if(res.data.code == 0){
      toolForm.icon_url = objectName;
      toolForm.image_url = res.data.data.data.url;
    }
  } else {
    ElMessage.error('上传失败')
  }
}

// 获取工具列表
const fetchTools = async () => {
  loading.value = true;
  
  // 添加超时保护
  let timeoutId = setTimeout(() => {
    loading.value = false;
    ElMessage.error('获取工具列表超时，请刷新页面重试');
  }, 15000); // 15秒超时
  
  try {
    const token = localStorage.getItem('token');
    
    // 使用AbortController允许取消请求
    const controller = new AbortController();
    const signal = controller.signal;
    
    // 5秒后如果请求未完成则取消
    const requestTimeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await requestV2('/system/tools/toolList', {
      signal
    });
    
    // 清除请求超时
    clearTimeout(requestTimeoutId);
    
    if (!response.data.code == 200) {
      const errorText = await response.text();
      console.error('获取工具列表错误:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`请求失败: ${response.status}`);
    }
    
    const data = await response.data.data;
    console.log('获取工具列表成功:', data.length, '个工具');
    
    // 使用Vue的过渡方式更新数据，避免直接替换导致的UI卡顿
    setTimeout(() => {
      tools.value = data;
      total.value = data.length;
    }, 100);
  } catch (error) {
    console.error('获取工具列表失败:', error);
    
    // 检查是否是取消错误
    if (error.name === 'AbortError') {
      ElMessage.error('获取列表超时，已取消请求');
    } else {
      ElMessage.error('获取工具列表失败，请稍后再试');
    }
  } finally {
    // 清除超时计时器
    clearTimeout(timeoutId);
    // 延迟关闭loading状态，确保UI有时间响应
    setTimeout(() => {
      loading.value = false;
    }, 200);
  }
};

// 显示添加工具对话框
const showAddToolDialog = () => {
  isEdit.value = false;
  resetForm();
  dialogVisible.value = true;
};

// 处理编辑工具
const handleEditTool = (tool) => {
  isEdit.value = true;
  resetForm();
  
  // 填充表单数据
  toolForm.id = tool.id;
  toolForm.name = tool.name;
  toolForm.description = tool.description || '';
  toolForm.icon_url = tool.icon_url || '';
  toolForm.download_url = tool.download_url || '';
  toolForm.sort_order = tool.sort_order || 0;
  toolForm.status = tool.status || 'active';
  toolForm.remove_icon = false;
  
  dialogVisible.value = true;
};

// 处理删除工具
const handleDeleteTool = (tool) => {
  currentTool.value = tool;
  deleteDialogVisible.value = true;
};

// 确认删除
const confirmDelete = async () => {
  if (!currentTool.value) return;
  
  deleteLoading.value = true;
  try {
    console.log('正在删除工具:', currentTool.value.id);
    
    const response = await fetch(`/api/tools/${currentTool.value.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    console.log('删除工具响应状态:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('删除工具失败:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`删除失败: ${response.status} - ${errorText || response.statusText}`);
    }
    
    ElMessage.success('工具删除成功');
    fetchTools(); // 重新加载列表
    deleteDialogVisible.value = false;
  } catch (error) {
    console.error('删除工具失败:', error);
    ElMessage.error('删除工具失败，请稍后再试');
  } finally {
    deleteLoading.value = false;
  }
};

// 重置表单
const resetForm = () => {
  if (toolFormRef.value) {
    toolFormRef.value.resetFields();
  }
  
  toolForm.id = null;
  toolForm.name = '';
  toolForm.description = '';
  toolForm.icon_url = '';
  toolForm.download_url = '';
  toolForm.sort_order = 0;
  toolForm.status = 'active';
  toolForm.icon_file = null;
  toolForm.remove_icon = false;
};

// 图标上传前的验证
const beforeIconUpload = (file) => {
  const isImage = /^image\/(jpeg|png|gif|svg\+xml|webp)$/.test(file.type);
  const isLt2M = file.size / 1024 / 1024 < 2;
  
  if (!isImage) {
    ElMessage.error('只能上传图片文件!');
    return false;
  }
  
  if (!isLt2M) {
    ElMessage.error('图片大小不能超过 2MB!');
    return false;
  }
  
  // 保存文件对象供后面使用
  toolForm.icon_file = file;
  // 创建临时URL用于预览
  toolForm.icon_url = URL.createObjectURL(file);
  
  return false; // 阻止默认上传行为
};

// 自定义上传方法（此方法不会实际上传，只是为了配合ElementUI的上传组件）
const uploadIcon = () => {
  // 这里什么也不做，因为我们会在表单提交时一起处理文件上传
};

// 移除图标
const removeIcon = () => {
  toolForm.icon_url = '';
  toolForm.icon_file = null;
  toolForm.remove_icon = true;
};

// 提交表单
const submitForm = async () => {
  if (!toolFormRef.value) return;
  
  // 添加超时保护
  let timeoutId = setTimeout(() => {
    submitLoading.value = false;
    ElMessage.error('操作超时，请重试');
  }, 15000); // 15秒超时
  
  try {
    await toolFormRef.value.validate();
    
    submitLoading.value = true;
    
    // 准备请求数据对象
    const formData = {
      name: toolForm.name,
      description: toolForm.description,
      icon_url: toolForm.icon_url,
      sort_order: toolForm.sort_order,
      status: toolForm.status
    };

    // 检查下载链接格式
    if (toolForm.download_url) {
      // 确保有前缀
      let downloadUrl = toolForm.download_url;
      if (!downloadUrl.startsWith('http://') && !downloadUrl.startsWith('https://') && downloadUrl.startsWith('www.')) {
        downloadUrl = 'http://' + downloadUrl;
      }
      formData.download_url = downloadUrl;
    }

    // 处理图标（注意：普通对象无法处理文件上传，此功能已移除）
    if (toolForm.remove_icon) {
      formData.remove_icon = 'true';
    }
    
    // 确定API路径和方法
    const url = isEdit.value ? `/system/tools/${toolForm.id}` : '/system/tools';
   
    
    // 发送请求，使用AbortController允许取消请求
    const controller = new AbortController();
    const signal = controller.signal;
    
    // 如果执行时间超过10秒，自动取消请求
    const requestTimeoutId = setTimeout(() => controller.abort(), 10000);
    
    // 发送请求
    // 设置JSON请求头
    const response = await requestV2.post(url, formData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // 清除请求超时
    clearTimeout(requestTimeoutId);
    
    console.log('服务器响应状态:', response.status);
    
    if (!response.status || !response.data.code == 200) {
      const errorText = await response.text();
      console.error('请求失败:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`请求失败: ${response.status} - ${errorText || response.data.msg}`);
    }
    
    const result = await response.data.data;
    
    ElMessage.success(isEdit.value ? '工具更新成功' : '工具添加成功');
    dialogVisible.value = false;
    
    // 延迟执行刷新列表，确保UI有时间完成转换
    setTimeout(() => {
      fetchTools(); // 重新加载列表
    }, 300);
    
  } catch (error) {
    console.error('提交表单失败:', error);
    
    // 检查是否是取消错误
    if (error.name === 'AbortError') {
      ElMessage.error('请求超时，已取消');
    } else {
      ElMessage.error(error.message || '操作失败，请检查表单并重试');
    }
  } finally {
    // 清除超时计时器
    clearTimeout(timeoutId);
    // 确保加载状态停止
    submitLoading.value = false;
  }
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchTools();
};

const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchTools();
};

onMounted(() => {
  fetchTools();
});
</script>

<style scoped>
.tool-management-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.tool-icon-uploader {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tool-icon-uploader:hover {
  border-color: #409EFF;
}

.tool-icon-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tool-icon-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.icon-actions {
  margin-top: 10px;
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f5f7fa;
  color: #909399;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>